import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { Loader } from '@googlemaps/js-api-loader'
import { UUID } from '../utils/util';

@Component({
  selector: 'udo-plugin-googlemap',
  template: `<div [attr.id]="id" style="width:100%;height:100%"></div>`
})
export class PluginGooglemapComponent implements OnInit, OnDestroy {

    public params;
    private eventSubscription: Subscription;
    public objectId;
    public id = `googlemap-${UUID()}`;
    public map;
    private google;
    private markers = [];
    private geocoder;

    constructor(
    ) {}
    
    @Input()
    public set entries(entries: any) {
      this.params = entries;
      this.objectId = entries.id;
      this.initMap();
    }

    @Input()
    public set eventReceiver(events: any) {
      this.events = events;
      this.initEventReceiver();
    }

    @Input() events: Observable<any>;
    @Output() errorEvents: EventEmitter<any> = new EventEmitter();
    @Output() stdEvents: EventEmitter<any> = new EventEmitter();

    ngOnInit() {
      this.initEventReceiver();
    }

    ngOnDestroy() {
      if (this.eventSubscription !== undefined) {
        this.eventSubscription.unsubscribe();
      }
    }

    initEventReceiver() {
      if (this.events !== undefined) {
        this.eventSubscription = this.events.subscribe((res) => {
          this.handleEntryEvent(res);
        })
      }
    }

    handleEntryEvent($event) {
      if ($event.type === 'addmarker') {
        this.addMarker($event.data);
      } else if ($event.type === 'fitbounds') {
        this.fitBounds();
      } else if ($event.type === 'removemarker') {
        this.removeMarker($event.data);
      }
    }

    handleMarkerClick(UUID, $event) {
      const marker = this.markers.find((f) => (f.uuid === UUID));
      if (marker !== undefined) {
        this.stdEvents.next({ type: 'workflow', data: {eventId: '1', data: [
          {id: '0', value: UUID},
          {id: '1', value: marker.data}
        ]}});
      }
    }

    handleMapClick($event) {
      this.stdEvents.next({ type: 'workflow', data: {eventId: '2', data: [
        {id: '0', value: $event.latLng.lat},
        {id: '1', value: $event.latLng.lng}
      ]}});
    }
    
    initMap() {
      setTimeout(() => {
        const key = this.params.api_key;
        const defaultLat = Number(this.params.default_lat);
        const defaultLng = Number(this.params.default_lng);
        const defaultZoom = Number(this.params.default_zoom);

        if (key === undefined || key === null || key === '') {
          this.errorEvents.emit(`Empty API KEY, please put your api key in the plugin options`);
          return;
        }

        let styles = [];
        if (this.params.styles !== '') {
          try {
            styles = JSON.parse(this.params.styles);
          } catch (e) {
            this.errorEvents.emit(`Error while parsing json styles options : ${e}`);
            return;
          }
        }
        if (isNaN(defaultLat)) {
          this.errorEvents.emit(`Default lat must be a number`);
          return;
        }
        if (isNaN(defaultLng)) {
          this.errorEvents.emit(`Default lng must be a number`);
          return;
        }
        if (isNaN(defaultZoom)) {
          this.errorEvents.emit(`Default zoom must be a number`);
          return;
        }

        const loader = new Loader({
          apiKey: key,
          version: "weekly",
          libraries: ["places"]
        });

        const mapOptions = {
          center: {
            lat: defaultLat,
            lng: defaultLng
          },
          zoom: defaultZoom,
          disableDefaultUI: this.params.hide_controls === "1",
          mapTypeId: this.params.maptype,
          streetViewControl: this.params.allow_streetview === "1",
          styles
        };

        loader.load().then(() => {
          const win:any = window;
          this.google = win.google;
          this.map = new this.google.maps.Map(document.getElementById(this.id), mapOptions);
          this.geocoder = new this.google.maps.Geocoder();
          this.afterInit();
        }).catch(e => {
          this.errorEvents.emit(`Error loading map : ${e}`);
          this.afterInit();
        });

      });
    }

    afterInit() {
      this.map.addListener("click", this.handleMapClick.bind(this));
      this.stdEvents.next({ type: 'workflow', data: {eventId: '0', data: null}});
    }


    async addMarker(options) {

      try {

        const marker: any = {};

        const origin = options.origin;

        
        if (origin === 'coordinates') {

          //From coordinates

          const lat = Number(options.lat);
          const lng = Number(options.lng);
    
          if (isNaN(lat)) {
            throw ('Lat must be a number');
          }
          if (isNaN(lng)) {
            throw ('Lng must be a number');
          }
    
          marker.position = { lat, lng };

        } else {

          //From address

          const address = String(options.address);

          if (address === '') {
            throw ('Empty address');
          }

          marker.position = await this.geocode(address);

        }

        

        const title = options.title;
        if (title !== undefined && title !== null && String(title) !== '') {
          marker.title = String(title)
        }

        switch (options.type) {
          case 'standard': {
            const label = options.label;
            if (label !== undefined && label !== null && label !== '') {
              marker.label = label;
            }
            break;
          }
          case 'image': {
            marker.icon = options.imageurl;
            break;
          }
          case 'svg': {
            const svgMarker = {
              path: options.svg_code,
              fillColor: options.svg_fillcolor,
              fillOpacity: Number(options.svg_fillopacity),
              strokeWeight: Number(options.svg_strokeweight),
              rotation: Number(options.svg_rotation),
              scale: Number(options.svg_scale)
            };
            marker.icon = svgMarker;
            break;
          }
        }


        console.log(marker)

        const GUID = UUID();
        const gmarker = new this.google.maps.Marker(marker);
        gmarker.setMap(this.map);
        this.google.maps.event.addListener(gmarker, 'click', this.handleMarkerClick.bind(this, GUID));

        this.markers.push({uuid: GUID, marker, gmarker, data: options.custom_data});
        this.stdEvents.next({ type: 'actionfinished', data: [GUID]});

      } catch (err) {
        this.errorEvents.emit(err);
      }
      
    }

    removeMarker(options) {
      const UUID = options.uuid;
      const marker = this.markers.find((f) => (f.uuid === String(UUID)));
      if (marker !== undefined) {
        marker.gmarker.setMap(null);
      } else {
        this.errorEvents.emit("Marker not found");
      }
    }

    fitBounds() {
      const bounds = new this.google.maps.LatLngBounds();
      for (const marker of this.markers) {
        bounds.extend(marker.marker.position);
      }
      this.map.fitBounds(bounds);
      this.map.panToBounds(bounds);
    }

    geocode(address): Promise<any> {
      return new Promise<any>((resolve, reject) => {
        this.geocoder.geocode({ 'address': address }, function (results, status) {
          if (status == 'OK') {
            const position = results[0].geometry.location;
            resolve(position);
          } else {
            let err = status;
            if (err === 'REQUEST_DENIED') {
              err += ': Geocoding API is not activated for this API key';
            } else if (err === 'ZERO_RESULTS') {
              err += ': no coordinates found for this address';
            }
            reject(err);
          }
        });
      });
    }
}
